import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Check, Loader2, X } from "lucide-react";
import { Order } from "../../SalesPage/SalesPageInterface";
import { dictionary } from "../../../utils/dictionary";
import { useAuth } from "../../../context/AuthContext";
import { formatPartialOrderHash } from "../../../utils/formatters";
import { LogisticState } from "../../../types/order/logisticState";
import { PaidState } from "../../../types/order/paidState";
import { putOrderLogisticState } from "../../../services/orders/orderAxios";
import { useOrderDetails } from "../../../context/OrderDetailContext";
import { ConfirmationInfo } from "../../../services/whatsapp/whatsappService";
import { StatusChip, StatusTone } from "@/components/status/StatusDot";
import { getPaymentTone } from "@/components/status/statusTones";
import { BaseModal } from "../../../components/modals/BaseModal";

const CONFIRMATION_CHIP_CONFIG: Record<string, { tone: StatusTone; label: Record<string, string> }> = {
  pending: { tone: "neutral", label: { en: "Pending", es: "Pendiente", pt: "Pendente" } },
  awaiting_response: { tone: "warning", label: { en: "Awaiting", es: "Esperando", pt: "Aguardando" } },
  confirmed: { tone: "success", label: { en: "Confirmed", es: "Confirmado", pt: "Confirmado" } },
  rejected: { tone: "error", label: { en: "Rejected", es: "Rechazado", pt: "Rejeitado" } },
  expired: { tone: "neutral", label: { en: "Expired", es: "Expirado", pt: "Expirado" } },
  canceled: { tone: "neutral", label: { en: "Canceled", es: "Cancelado", pt: "Cancelado" } },
  send_failed: { tone: "error", label: { en: "Send failed", es: "Error de envío", pt: "Erro de envio" } },
};

export const OrderFulfillmentAction = ({
  order,
  className = "",
  onChanged,
}: {
  order: Order;
  className?: string;
  // El contexto de detalle solo actualiza la orden abierta. Quien tenga una lista
  // detras (la tabla de /orders) necesita recargarla: su `logistics_status_label`
  // lo calcula el backend y queda viejo tras el toggle.
  onChanged?: () => void;
}) => {
  const { language } = useAuth();
  const { changeOrderFulfillmentState } = useOrderDetails();
  const [isUpdating, setIsUpdating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isFulfilled = order.logistic_state === LogisticState.IN_PROGRESS;
  const canToggleFulfillment =
    order.logistic_state === LogisticState.CREATED || isFulfilled;
  // Hay un proveedor de orden reportando el estado real: un cambio manual puede chocar.
  const hasSyncedProvider = !!order.fulfillment_provider;

  const applyToggle = async () => {
    if (isUpdating || !canToggleFulfillment) return;
    setIsUpdating(true);
    const nextState = isFulfilled ? LogisticState.CREATED : LogisticState.IN_PROGRESS;
    const response = await putOrderLogisticState(nextState, order.hash_id);
    if (response) {
      changeOrderFulfillmentState(nextState === LogisticState.IN_PROGRESS ? "fulfilled" : "unfulfilled");
      onChanged?.();
    }
    setIsUpdating(false);
  };

  const handleFulfillmentToggle = () => {
    if (isUpdating || !canToggleFulfillment) return;
    if (hasSyncedProvider) {
      setConfirmOpen(true);
      return;
    }
    applyToggle();
  };

  if (!canToggleFulfillment) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleFulfillmentToggle}
        disabled={isUpdating}
        className={`inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
          isFulfilled
            ? "border border-gray-200 bg-gray-100 text-gray-700 hover:bg-gray-200"
            : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
        } ${className}`}
      >
        {isUpdating ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isFulfilled ? (
          <X className="h-4 w-4" />
        ) : (
          <Check className="h-4 w-4" />
        )}
        <span>
          {isFulfilled
            ? dictionary["unfulfill-item"][language]
            : dictionary["fulfill-item"][language]}
        </span>
      </button>

      <BaseModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        imageSrc="https://fluxi.s3.amazonaws.com/prod/assets/media/warning-purple.gif"
        imageAlt="Warning"
        title={dictionary["delivery-manual-override-title"][language]}
        description={dictionary["delivery-manual-override-desc"][language]}
        buttonOne={{
          text: dictionary["delivery-continue"][language],
          onClick: () => {
            setConfirmOpen(false);
            applyToggle();
          },
          loading: isUpdating,
          disabled: isUpdating,
        }}
        buttonTwo={{
          text: dictionary["delivery-cancel"][language],
          onClick: () => setConfirmOpen(false),
        }}
      />
    </>
  );
};

export const OrderTopBar = ({
  order,
  confirmationInfo,
  onBack,
  compact = false,
  showFulfillmentAction = true,
}: {
  order: Order;
  confirmationInfo: ConfirmationInfo | null;
  onBack?: () => void;
  compact?: boolean;
  showFulfillmentAction?: boolean;
}) => {
  const { language } = useAuth();
  const location = useLocation();
  const isFulfilled = order.logistic_state === LogisticState.IN_PROGRESS;
  const isPaid = order.payment_state === PaidState.PAID;
  const orderReference = order.display_code || formatPartialOrderHash(order.hash_id?.toString() || "");
  const langKey = language === "en" ? "en" : language === "pt" ? "pt" : "es";
  const confirmationConfig = confirmationInfo
    ? CONFIRMATION_CHIP_CONFIG[confirmationInfo.status]
    : null;

  return (
    <div className="sticky top-0 z-20 bg-[#F5F6FA]/95 backdrop-blur">
      <div className={`flex items-start gap-3 py-4 ${compact ? "px-4" : "px-4 md:px-16"}`}>
        {!compact && onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-200/60 hover:text-gray-900"
            aria-label="Cerrar detalle"
          >
            <ArrowLeft size={18} />
          </button>
        ) : !compact ? (
          <Link
            to={`/orders${location.search}`}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-200/60 hover:text-gray-900"
          >
            <ArrowLeft size={18} />
          </Link>
        ) : null}

        <div
          className={
            compact
              ? "min-w-0 flex-1 pr-10"
              : "flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2"
          }
        >
          <p className={`${compact ? "truncate text-xl" : "text-lg"} font-bold leading-tight text-gray-900`}>
            {compact ? "Orden: " : "#"}
            {orderReference}
          </p>

          {!compact && (
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {order.payment_state && (
                <StatusChip
                  tone={getPaymentTone(order.payment_state)}
                  label={
                    isPaid
                      ? dictionary["paid-state"][language]
                      : order.payment_state === PaidState.UNPAID
                        ? dictionary["unpaid-state"][language]
                        : dictionary["pending-state"][language]
                  }
                />
              )}
              <StatusChip
                tone={isFulfilled ? "success" : "neutral"}
                label={
                  isFulfilled
                    ? dictionary["fulfilled-state"][language]
                    : dictionary["unfulfilled-state"][language]
                }
              />
              {Boolean(order.priority) && (
                <StatusChip tone="violet" label={dictionary["priority"][language]} />
              )}
              {confirmationConfig && (
                <StatusChip tone={confirmationConfig.tone} label={confirmationConfig.label[langKey]} />
              )}
            </div>
          )}
        </div>

        {showFulfillmentAction && (
          <OrderFulfillmentAction order={order} className="ml-auto" />
        )}
      </div>
    </div>
  );
};
